import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import {
  createContext,
  forwardRef,
  useContext,
  useMemo,
  type ComponentPropsWithoutRef,
  type ReactElement,
  type ReactNode,
} from 'react';

import { Kbd } from '@/components/ui/Kbd';
import { cn } from '@/lib/cn';

/**
 * Tooltip.
 *
 * - 400 ms open delay by default (`TOOLTIP_DELAY`).
 * - 0 ms when the pointer moves between neighbouring triggers — that is Radix's
 *   `skipDelayDuration`, which lives on the *provider*, so the app shell should mount
 *   <TooltipProvider> once at the root. A bare <Tooltip> still works: it provisions its
 *   own provider so it can never throw, it simply loses the shared skip window.
 * - Optional `shortcut`, rendered as <Kbd> chips on the trailing edge.
 */

export const TOOLTIP_DELAY = 400;
const SKIP_WINDOW = 320;

const HasProviderContext = createContext(false);

export interface TooltipProviderProps {
  children: ReactNode;
  delayDuration?: number;
  skipDelayDuration?: number;
  disableHoverableContent?: boolean;
}

/** Mount once, high in the app tree, so neighbouring tooltips share the skip window. */
export function TooltipProvider({
  children,
  delayDuration = TOOLTIP_DELAY,
  skipDelayDuration = SKIP_WINDOW,
  disableHoverableContent = true,
}: TooltipProviderProps) {
  return (
    <HasProviderContext.Provider value={true}>
      <TooltipPrimitive.Provider
        delayDuration={delayDuration}
        skipDelayDuration={skipDelayDuration}
        disableHoverableContent={disableHoverableContent}
      >
        {children}
      </TooltipPrimitive.Provider>
    </HasProviderContext.Provider>
  );
}

export type TooltipSide = 'top' | 'right' | 'bottom' | 'left';
export type TooltipAlign = 'start' | 'center' | 'end';

export interface TooltipProps {
  /** Tooltip body. `null`/`undefined`/`false` renders the trigger untouched. */
  content: ReactNode;
  children: ReactElement;
  side?: TooltipSide;
  align?: TooltipAlign;
  sideOffset?: number;
  /** Open delay in ms. */
  delay?: number;
  /** Keyboard hint. Prefer logical keys (`'mod+k'`, `['shift','enter']`); glyphs also work. */
  shortcut?: string | string[];
  open?: boolean;
  defaultOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  /** Renders `children` alone — for conditionally-explained controls. */
  disabled?: boolean;
  className?: string;
  /** Keep the tooltip open while the pointer is over it (only for tooltips with links). */
  hoverable?: boolean;
}

const tooltipContentClass = cn(
  'z-[var(--z-tooltip)] max-w-[280px] select-none',
  'rounded-[var(--radius-sm)] border border-line bg-[var(--surface-overlay)]',
  'px-2 py-1 text-sm leading-snug text-primary shadow-[var(--shadow-3)]',
  'origin-[var(--radix-tooltip-content-transform-origin)]',
  'data-[state=delayed-open]:animate-[af-scale-in_var(--dur-fast)_var(--ease-out)]',
  'data-[state=instant-open]:animate-[af-fade-in_var(--dur-instant)_var(--ease-out)]',
);

export function Tooltip({
  content,
  children,
  side = 'top',
  align = 'center',
  sideOffset = 6,
  delay = TOOLTIP_DELAY,
  shortcut,
  open,
  defaultOpen,
  onOpenChange,
  disabled,
  className,
  hoverable = false,
}: TooltipProps) {
  const hasProvider = useContext(HasProviderContext);
  const keys = useMemo(() => parseShortcut(shortcut), [shortcut]);

  if (disabled || content === null || content === undefined || content === false) {
    return children;
  }

  const body = (
    <TooltipPrimitive.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delayDuration={delay}
      disableHoverableContent={!hoverable}
    >
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={8}
          className={cn(tooltipContentClass, keys && 'flex items-center gap-2', className)}
        >
          <span className="min-w-0">{content}</span>
          {keys ? <Kbd keys={keys} /> : null}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );

  if (hasProvider) return body;

  return (
    <TooltipPrimitive.Provider delayDuration={delay} skipDelayDuration={SKIP_WINDOW}>
      {body}
    </TooltipPrimitive.Provider>
  );
}

/** `'⌘K'` → `['⌘','K']`, `'Shift+Enter'` → `['Shift','Enter']`. */
export function parseShortcut(shortcut?: string | string[]): string[] | null {
  if (!shortcut) return null;
  if (Array.isArray(shortcut)) return shortcut.length ? shortcut : null;
  const byDelimiter = shortcut.split(/[+\s]+/).filter(Boolean);
  if (byDelimiter.length > 1) return byDelimiter;
  const glyphs = shortcut.match(/[⌘⌥⌃⇧⏎⌫↑↓←→]|[^⌘⌥⌃⇧⏎⌫↑↓←→]+/g);
  return glyphs && glyphs.length ? glyphs : [shortcut];
}

/**
 * Escape hatch for anchoring a tooltip to something that is not a simple trigger
 * (a virtual element, a cell inside the grid). Prefer <Tooltip>.
 */
export const TooltipContent = forwardRef<
  HTMLDivElement,
  ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(function TooltipContent({ className, ...rest }, ref) {
  return (
    <TooltipPrimitive.Content
      ref={ref}
      collisionPadding={8}
      className={cn(tooltipContentClass, className)}
      {...rest}
    />
  );
});

export const TooltipRoot = TooltipPrimitive.Root;
export const TooltipTrigger = TooltipPrimitive.Trigger;
export const TooltipPortal = TooltipPrimitive.Portal;
